import { fetchAuthSession } from 'aws-amplify/auth';
import { Match, Prediction, PickResult, LeaderboardEntry, UserProfile, AdminUser, Squad, GroupStanding, MatchResult } from './types';

const API_URL =
  process.env.REACT_APP_API_URL ??
  'https://oxrlxnea49.execute-api.us-west-2.amazonaws.com/v1';

async function authHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export async function getMatches(): Promise<Match[]> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/matches`, { headers });
    if (!res.ok) throw new Error(`API returned ${res.status}`);
    return res.json() as Promise<Match[]>;
  } catch (err) {
    console.warn('getMatches error', err);
    return [];
  }
}

export async function postPick(
  matchId: string,
  pick: 'home' | 'draw' | 'away',
  matchMeta: {
    homeTeam: string; awayTeam: string; competition: string;
    kickoffTime: string; homeTeamLogo: string; awayTeamLogo: string;
    revealed?: boolean; challenged?: boolean; aiChoice?: string;
  }
): Promise<{ success: boolean }> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    const claims = session.tokens?.idToken?.payload;
    const email = claims?.email as string | undefined;
    const cognitoUsername = claims?.['cognito:username'] as string | undefined;
    const displayName = email?.split('@')[0] ?? cognitoUsername ?? '';

    const res = await fetch(`${API_URL}/picks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ matchId, pick, displayName, ...matchMeta }),
    });
    if (!res.ok) throw new Error(`API returned ${res.status}`);
    return res.json();
  } catch {
    return { success: true }; // optimistic — UI already updated
  }
}

export async function getMyPicks(): Promise<PickResult[]> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/my-picks`, { headers });
    if (!res.ok) throw new Error(`API returned ${res.status}`);
    return res.json();
  } catch {
    return [];
  }
}

export async function getLeaderboard(): Promise<LeaderboardEntry[]> {
  try {
    const res = await fetch(`${API_URL}/leaderboard`);
    if (!res.ok) throw new Error(`API returned ${res.status}`);
    return res.json();
  } catch {
    return [];
  }
}

export async function getUserProfile(): Promise<UserProfile | null> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/profile`, { headers });
    if (!res.ok) throw new Error(`API returned ${res.status}`);
    return res.json();
  } catch {
    return null;
  }
}

export async function updateUserProfile(
  updates: {
    displayName?: string;
    supportingCountry?: string;
    supportingCountryName?: string;
    goldenBoot?: string;
    goldenBall?: string;
    goldenGlove?: string;
  }
): Promise<boolean> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(updates),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function getAdminUsers(): Promise<{ users: AdminUser[]; total: number } | null> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/admin/users`, { headers });
    if (!res.ok) throw new Error(`API returned ${res.status}`);
    return res.json();
  } catch {
    return null;
  }
}

export async function submitFeedback(
  message: string,
  category: 'bug' | 'idea' | 'other',
  page: string
): Promise<boolean> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ message, category, page }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function getMySquads(): Promise<Squad[]> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/squads/my`, { headers });
    if (!res.ok) throw new Error(`API returned ${res.status}`);
    return res.json();
  } catch {
    return [];
  }
}

export async function createSquad(name: string, displayName: string): Promise<Squad | null> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/squads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ name, displayName }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as any).error ?? `API returned ${res.status}`);
    }
    return res.json();
  } catch (e) {
    throw e;
  }
}

export async function joinSquad(inviteCode: string, displayName: string): Promise<Squad | null> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/squads/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ inviteCode, displayName }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as any).error ?? `API returned ${res.status}`);
    }
    return res.json();
  } catch (e) {
    throw e;
  }
}

export async function getSquadLeaderboard(squadId: string): Promise<LeaderboardEntry[]> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/squads/leaderboard?squadId=${encodeURIComponent(squadId)}`, { headers });
    if (!res.ok) throw new Error(`API returned ${res.status}`);
    return res.json();
  } catch {
    return [];
  }
}

// Legacy — kept for any remaining direct AI analysis calls
export async function postPrediction(
  matchId: string,
  pick: 'home' | 'draw' | 'away',
  userId: string
): Promise<{ success: boolean }> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/predict`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ matchId, pick, userId }),
    });
    if (!res.ok) throw new Error(`API returned ${res.status}`);
    return res.json();
  } catch {
    return { success: true };
  }
}

export async function getStandings(): Promise<GroupStanding[]> {
  const res = await fetch(`${API_URL}/standings`);
  if (!res.ok) return [];
  return res.json() as Promise<GroupStanding[]>;
}

// WC 2026 group stage: June 11 → July 2
const WC_START = new Date('2026-06-11');

function datesBetweenStartAndToday(): string[] {
  const dates: string[] = [];
  const end = new Date();
  end.setDate(end.getDate() + 1); // +1 day to catch late-starting matches
  const cur = new Date(WC_START);
  while (cur <= end) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, '0');
    const d = String(cur.getDate()).padStart(2, '0');
    dates.push(`${y}${m}${d}`);
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

interface EspnEvent {
  id: string;
  date: string;
  status?: { type?: { name?: string; description?: string }; displayClock?: string };
  competitions?: Array<{
    competitors?: Array<{ homeAway: string; team?: { displayName?: string; logo?: string }; score?: string; winner?: boolean }>;
    venue?: { fullName?: string };
  }>;
}

export async function getResults(): Promise<MatchResult[]> {
  const dates = datesBetweenStartAndToday();
  const pages = await Promise.all(
    dates.map(d =>
      fetch(`https://site.api.espn.com/apis/site/v2/sports/soccer/fifa.world/scoreboard?dates=${d}`)
        .then(r => r.json() as Promise<{ events?: EspnEvent[] }>)
        .then(b => b.events ?? [])
        .catch(() => [] as EspnEvent[])
    )
  );

  const seen = new Set<string>();
  const results: MatchResult[] = [];

  for (const events of pages) {
    for (const e of events) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);

      const statusName = e.status?.type?.name ?? '';
      const isFinished = statusName === 'STATUS_FULL_TIME';
      const isLive = !isFinished && statusName !== 'STATUS_SCHEDULED' && statusName !== '';
      if (!isFinished && !isLive) continue; // skip scheduled

      const comp = e.competitions?.[0];
      const home = comp?.competitors?.find(t => t.homeAway === 'home');
      const away = comp?.competitors?.find(t => t.homeAway === 'away');
      if (!home || !away) continue;

      let statusDisplay = 'FT';
      if (isLive) {
        statusDisplay = e.status?.displayClock
          ? `${e.status.displayClock}'`
          : statusName.includes('HALFTIME') ? 'HT' : 'LIVE';
      }

      results.push({
        id: e.id,
        date: e.date,
        status: isFinished ? 'finished' : 'live',
        statusDisplay,
        home: {
          name: home.team?.displayName ?? '',
          logo: home.team?.logo ?? '',
          score: parseInt(home.score ?? '0') || 0,
          winner: home.winner === true,
        },
        away: {
          name: away.team?.displayName ?? '',
          logo: away.team?.logo ?? '',
          score: parseInt(away.score ?? '0') || 0,
          winner: away.winner === true,
        },
        venue: comp?.venue?.fullName ?? '',
      });
    }
  }

  return results.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()); // newest first
}
